import { useState, useEffect } from "react";
import PlatformLayout from "../components/PlatformLayout";
import { getPlans, createPlan, updatePlan, deletePlan, type Plan } from "@/utils/platformApi";

export default function PlatformPlansPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [toast, setToast] = useState("");

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await getPlans();
      setPlans(res.list);
    } catch (err: any) {
      console.error('获取套餐列表失败:', err);
      showToast(err.message || "获取数据失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSave = async (data: any) => {
    try {
      if (editingPlan) {
        await updatePlan(editingPlan.id, data);
        showToast("套餐已更新");
      } else {
        await createPlan(data);
        showToast("套餐创建成功");
      }
      setShowModal(false);
      setEditingPlan(null);
      fetchPlans();
    } catch (err: any) {
      showToast(err.message || "操作失败");
    }
  };

  const handleToggleStatus = async (plan: Plan) => {
    const newStatus = plan.status === 'active' ? 'inactive' : 'active';
    try {
      await updatePlan(plan.id, { status: newStatus });
      showToast(newStatus === 'active' ? "套餐已上架" : "套餐已下架");
      fetchPlans();
    } catch (err: any) {
      showToast(err.message || "操作失败");
    }
  };

  const handleDelete = async (planId: string) => {
    if (!confirm("确定要删除此套餐吗？")) return;
    try {
      await deletePlan(planId);
      showToast("套餐已删除");
      fetchPlans();
    } catch (err: any) {
      showToast(err.message || "删除失败");
    }
  };

  const activeCount = plans.filter(p => p.status === 'active').length;
  const totalAgentCount = plans.reduce((sum, p) => sum + p.agent_count, 0);

  return (
    <PlatformLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">套餐管理</h2>
            <p className="text-sm text-gray-500 mt-1">配置代理商可购买的套餐</p>
          </div>
          <button
            onClick={() => {
              setEditingPlan(null);
              setShowModal(true);
            }}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-blue-500 text-white text-sm font-medium rounded-xl hover:bg-blue-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            新建套餐
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-gray-800">{plans.length}</div>
            <div className="text-sm text-gray-500">套餐总数</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-green-600">{activeCount}</div>
            <div className="text-sm text-gray-500">在售</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-blue-600">{totalAgentCount}</div>
            <div className="text-sm text-gray-500">购买代理商</div>
          </div>
        </div>

        {/* Plans */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-2xl text-gray-400" />
          </div>
        ) : plans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {plans.map((plan) => {
              const benefits = plan.benefits || {};
              const features = benefits.features || [];
              const siteLimit = plan.site_limit ?? benefits.site_limit ?? -1;
              const isActive = plan.status === 'active';
              
              return (
                <div key={plan.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className={`p-5 ${isActive ? "bg-blue-500" : "bg-gray-400"} text-white`}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold">{plan.name}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${isActive ? "bg-white/20" : "bg-gray-300 text-gray-600"}`}>
                        {isActive ? "在售" : "已下架"}
                      </span>
                    </div>
                    <div className="mt-3">
                      <span className="text-3xl font-bold">¥{plan.amount}</span>
                      <span className="text-sm opacity-80">/年</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="text-sm text-gray-500 mb-3">
                      <span className="font-medium text-gray-800">
                        {siteLimit === -1 ? "无限" : siteLimit}
                      </span> 个站点
                    </div>
                    {features.length > 0 && (
                      <ul className="space-y-2 mb-5">
                        {features.slice(0, 5).map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                            <i className="ri-check-line text-green-500" />
                            {feature}
                          </li>
                        ))}
                        {features.length > 5 && (
                          <li className="text-sm text-gray-400">等 {features.length} 项功能</li>
                        )}
                      </ul>
                    )}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-sm text-gray-500">{plan.agent_count} 个代理商使用</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingPlan(plan);
                            setShowModal(true);
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => handleToggleStatus(plan)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                            isActive
                              ? "text-red-600 hover:bg-red-50"
                              : "text-green-600 hover:bg-green-50"
                          }`}
                        >
                          {isActive ? "下架" : "上架"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <i className="ri-vip-crown-line text-3xl mb-2" />
            <p className="text-sm">暂无套餐，请先创建</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <PlanModal
          plan={editingPlan}
          onClose={() => {
            setShowModal(false);
            setEditingPlan(null);
          }}
          onSave={handleSave}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-gray-800 text-white text-sm rounded-xl shadow-lg">
          <i className="ri-checkbox-circle-line text-green-400" />
          {toast}
        </div>
      )}
    </PlatformLayout>
  );
}

function PlanModal({
  plan,
  onClose,
  onSave,
}: {
  plan?: Plan | null;
  onClose: () => void;
  onSave: (data: any) => void;
}) {
  const benefits = plan?.benefits || {};
  const [form, setForm] = useState({
    name: plan?.name || "",
    amount: plan?.amount || 0,
    site_limit: plan?.site_limit ?? benefits.site_limit ?? -1,
    features: (benefits.features || []).join("\n"),
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError("请输入套餐名称"); return; }
    if (form.amount < 0) { setError("价格不能为负数"); return; }

    setSubmitting(true);
    try {
      await onSave({
        name: form.name.trim(),
        amount: form.amount,
        site_limit: form.site_limit,
        features: form.features.split("\n").filter(Boolean),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">{plan ? "编辑套餐" : "新建套餐"}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">套餐名称</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="如：专业版"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">年费价格 (元)</label>
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
              placeholder="0"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">站点数量限制 (-1为无限)</label>
            <input
              type="number"
              value={form.site_limit}
              onChange={(e) => setForm({ ...form, site_limit: Number(e.target.value) })}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">功能列表 (每行一个)</label>
            <textarea
              value={form.features}
              onChange={(e) => setForm({ ...form, features: e.target.value })}
              rows={4}
              placeholder={"5个站点\n高级模板\n全部功能"}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 cursor-pointer"
          >
            {submitting ? '处理中...' : (plan ? '保存' : '创建')}
          </button>
        </div>
      </div>
    </div>
  );
}
